import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import StakeLoading from "@/components/StakeLoading";
import NoData from "@/components/NoData";
import LobbyMatchLeagues from "../LobbyMatchLeagues";
import LobbyMatchV28 from "./LobbyMatch";
import LobbyDateTabs from "../LobbyDateTabs";
import LobbyLeagueFilterV9 from "../LobbyLeagueFilterV9";
import LoadMoreButton from "../LoadMoreButton";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { buildThemeStyle } from "./buildThemeStyle";
import "./index.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV28 = () => {
  const compConfig = useCompConfig();
  const { hasDataConfig } = compConfig;
  const themeStyle = buildThemeStyle(compConfig, hasDataConfig);

  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status = selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  const visibleMatches = useMemo(() => {
    return allFilteredMatches.slice(0, displayLimit);
  }, [allFilteredMatches, displayLimit]);

  useEffect(() => {
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20);
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20);
  };

  const leaguesData = useMemo(() => {
    return baseLeaguesWithMatches.map((item) => ({
      id: item.league?.id || item.id,
      name: item.league?.name || item.name,
      logo: item.league?.logo || item.logo,
    }));
  }, [baseLeaguesWithMatches]);

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 md:rounded-xl px-0.5 md:px-2 py-3 bg-[var(--theme-bg)]"
      style={themeStyle}
    >
      <div className="space-y-2 rounded-lg">
        <div
          className="kbv-v28-header flex flex-col md:flex-row md:flex-nowrap items-center justify-between h-auto md:h-[76px] p-2 md:px-6 md:py-0 gap-4 relative rounded-tl-lg rounded-tr-lg border border-[var(--theme-border)] border-b-0"
          style={themeStyle}
        >
          <div className="flex w-full md:w-[78%] items-center overflow-x-auto gap-4">
            <LobbyDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              compact
            />
          </div>
          <div className="flex w-full md:w-[10%] shrink-0 items-center justify-center md:justify-end">
            <LobbyLeagueFilterV9
              selectedIds={selectedLeagueIds}
              onChange={handleLeagueFilterChange}
              className="h-full w-full md:w-[160px]"
              themeStyle={themeStyle}
            />
          </div>
        </div>

        {loading && <StakeLoading />}
        {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
        {!loading && !error && (
          <>
            {baseLeaguesWithMatches.length === 0 ? (
              <NoData message="Không có trận đấu nào" />
            ) : filteredLeaguesWithMatches.length === 0 ? (
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            ) : (
              <LobbyMatchLeagues
                leagues={leaguesData}
                matches={visibleMatches}
                render={(match, idx) =>
                  match ? (
                    <LobbyMatchV28
                      key={match.id || idx}
                      match={match}
                      isOdd={idx % 2 === 0}
                    />
                  ) : null
                }
              />
            )}
            <LoadMoreButton
              isLoading={isLoadingMore}
              onClick={() => setDisplayLimit((prev) => prev + 20)}
              loadingComponent={<StakeLoading />}
              hasMore={allFilteredMatches.length > displayLimit || hasMore}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default KbvTiLeV28;
